import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
  Tooltip,
  TableContainer,
  Autocomplete,
  TextField,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useReportsHubBack } from './hub/useReportsHubBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import ownAuthSession from '../../services/auth/ownAuthSession';
import firebaseDataService from '../../services/FirebaseDataService';
import {
  endOfDayReportApi,
  EndOfDayReportDto,
} from '../../services/restaurantApiService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

// ==================== TYPES ====================

interface EndOfDayReportRow {
  id: number;
  rowNumber: number; // Sıra numarası
  screenType: string;
  screenId: number | null;
  startDate: string;
  endDate: string;
  balance: number;
  cashTotal: number;
  posTotal: number;
  otherTotal: number; // ✅ YENİ: Diğer ödeme yöntemleri (yemek kartı, hediye kartı vb.)
  cancelledTotal: number;
  transactionCount: number;
  closedByName: string | null;
  closedByEmail: string | null;
  createdAt: string;
  notes: string | null;
  branchId: string | null; // Şube ID
  screensJson: string | null; // Ekranlar JSON
}

// ==================== HELPER FUNCTIONS ====================

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount) + ' ₺';
};

const formatDateTime = (dateString: string | Date, addTurkeyOffset = true): string => {
  let date = new Date(dateString);
  // UTC'den gelen tarihlere +3 saat ekle (Türkiye saati)
  if (addTurkeyOffset) {
    date = new Date(date.getTime() + (3 * 60 * 60 * 1000));
  }
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

// ==================== PROPS ====================

interface EndOfDayReportsListProps {
  isManager?: boolean;
  managerBranchId?: string | null;
}

// ==================== MAIN COMPONENT ====================

const EndOfDayReportsList: React.FC<EndOfDayReportsListProps> = ({ isManager = false, managerBranchId }) => {
  const { t } = useTranslation('salesSystem');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { goBack } = useReportsHubBack('/gun-sonlari', () => navigate(-1));
  
  // ✅ YENİ: AuthContext'ten şube bilgileri
  const { branches, getBranchName, userDetails } = useAuth();
  
  // ✅ Manager rolü kontrolü - prop üzerinden VEYA userDetails.role'dan
  const isManagerRole = isManager || userDetails?.role === 'manager';
  const effectiveBranchId = managerBranchId || (userDetails?.role === 'manager' ? userDetails?.branchesId : undefined);

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Filtre state'leri
  // ✅ Manager ise şubesini direkt kilitle, değilse 'ALL'
  const [selectedBranch, setSelectedBranch] = useState<string>(
    isManagerRole && effectiveBranchId ? effectiveBranchId : 'ALL'
  );
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Son 30 gün
    date.setHours(0, 0, 0, 0);
    return date;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  
  // Veri state'leri
  const [reports, setReports] = useState<EndOfDayReportRow[]>([]);
  
  // Sayfalama
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // ✅ Manager ise şube filtresini kendi şubesine kilitle
  useEffect(() => {
    if (isManagerRole && effectiveBranchId && selectedBranch !== effectiveBranchId) {
      setSelectedBranch(effectiveBranchId);
    }
  }, [isManagerRole, effectiveBranchId]);
  
  // Excel indirme
  const [loadingExport, setLoadingExport] = useState(false);

  // İlk yüklemede şirket bilgilerini al
  const loadInitialData = useCallback(async () => {
    try {
      // Kullanıcı bilgilerini al
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError(t('endOfDayReportsList.errors.userSessionMissing'));
        return;
      }

      // Şirket ID'sini al
      const userData = await firebaseDataService.getUserData(uid);
      if (!userData) {
        setError(t('endOfDayReportsList.errors.userInfoNotFound'));
        return;
      }

      const compId = userData.companyId;
      setCompanyId(compId ?? null);

      if (!compId) {
        setError(t('endOfDayReportsList.errors.companyNotFound'));
        return;
      }
      
    } catch (err: any) {
      console.error('İlk veri yükleme hatası:', err);
      setError(t('endOfDayReportsList.errors.dataLoadFailed', { message: err.message }));
    }
  }, [t]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Filtreleme işlemi
  const handleFilter = async () => {
    if (!companyId) {
      setError(t('endOfDayReportsList.errors.companyNotFoundRefresh'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // API'den gün sonu raporlarını çek
      const response = await endOfDayReportApi.getByCompanyId(
        startDate?.toISOString(),
        endDate?.toISOString()
      );
      
      // Şubeye göre filtrele
      let filteredReports = response.Data;
      if (selectedBranch !== 'ALL') {
        filteredReports = filteredReports.filter(r => r.BranchId === selectedBranch);
      }
      
      // Verileri satır formatına dönüştür
      const rows: EndOfDayReportRow[] = filteredReports.map((r, index) => ({
        id: r.Id,
        rowNumber: index + 1, // Sıra numarası
        screenType: r.ScreenType,
        screenId: r.ScreenId || null,
        startDate: r.StartDate,
        endDate: r.EndDate,
        balance: r.Balance,
        cashTotal: r.CashTotal,
        posTotal: r.PosTotal,
        otherTotal: r.OtherTotal || 0, // ✅ YENİ: Diğer ödeme yöntemleri
        cancelledTotal: r.CancelledTotal,
        transactionCount: r.TransactionCount,
        closedByName: r.ClosedByName || null,
        closedByEmail: r.ClosedByEmail || null,
        createdAt: r.CreatedAt,
        notes: r.Notes || null,
        branchId: r.BranchId || null, // Şube ID
        screensJson: r.ScreensJson || null, // Ekranlar JSON
      }));
      
      // En yeni en üstte
      rows.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      // ✅ YENİ: Sıralama sonrası rowNumber güncelle
      rows.forEach((row, index) => {
        row.rowNumber = index + 1;
      });
      
      setReports(rows);
      setDataLoaded(true);
      setPage(0);
      
      console.log(`✅ ${rows.length} gün sonu raporu yüklendi`);
    } catch (err: any) {
      console.error('Veri çekme hatası:', err);
      setError(err.message || t('endOfDayReportsList.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Sayfalama
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sayfalanmış veriler
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return reports.slice(startIndex, startIndex + rowsPerPage);
  }, [reports, page, rowsPerPage]);

  // Genel toplamları hesapla
  const totals = useMemo(() => {
    return reports.reduce(
      (acc, row) => ({
        balance: acc.balance + row.balance,
        cashTotal: acc.cashTotal + row.cashTotal,
        posTotal: acc.posTotal + row.posTotal,
        otherTotal: acc.otherTotal + row.otherTotal,
        cancelledTotal: acc.cancelledTotal + row.cancelledTotal,
        transactionCount: acc.transactionCount + row.transactionCount,
      }),
      { balance: 0, cashTotal: 0, posTotal: 0, otherTotal: 0, cancelledTotal: 0, transactionCount: 0 }
    );
  }, [reports]);

  // Excel'e aktar
  const handleExportExcel = async () => {
    if (!dataLoaded || reports.length === 0) {
      setError(t('endOfDayReportsList.errors.filterFirst'));
      return;
    }

    setLoadingExport(true);
    
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('endOfDayReportsList.title'));
      
      // Başlık
      worksheet.mergeCells('A1:K1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = t('endOfDayReportsList.title');
      titleCell.style = {
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'center' },
      };
      
      // Tarih bilgisi
      worksheet.mergeCells('A2:K2');
      const dateCell = worksheet.getCell('A2');
      dateCell.value = `${startDate ? formatDateTime(startDate) : ''} - ${endDate ? formatDateTime(endDate) : ''}`;
      dateCell.style = {
        font: { size: 12 },
        alignment: { horizontal: 'center' },
      };
      
      worksheet.addRow([]);
      
      // Sütun başlıkları
      const headerRow = worksheet.addRow([
        t('endOfDayReportsList.table.order'),
        t('endOfDayReportsList.table.branch'),
        t('endOfDayReportsList.filters.startDate'),
        t('endOfDayReportsList.filters.endDate'),
        t('endOfDayReportsList.table.balance'),
        t('endOfDayReportsList.table.cash'),
        'POS',
        t('endOfDayReportsList.table.other'),
        t('endOfDayReportsList.table.cancelled'),
        t('endOfDayReportsList.table.transactionCount'),
        t('endOfDayReportsList.table.createdAt'),
      ]);
      headerRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } },
          alignment: { horizontal: 'center' },
        };
      });
      
      // Veri satırları
      reports.forEach((row) => {
        worksheet.addRow([
          row.rowNumber,
          row.branchId ? getBranchName(row.branchId) : t('endOfDayReportsList.common.all'),
          formatDateTime(row.startDate),
          formatDateTime(row.endDate),
          row.balance,
          row.cashTotal,
          row.posTotal,
          row.otherTotal,
          row.cancelledTotal,
          row.transactionCount,
          formatDateTime(row.createdAt),
        ]);
      });
      
      // Boş satır ve GENEL TOPLAM satırı
      worksheet.addRow([]);
      const totalRow = worksheet.addRow([
        '',
        '',
        '',
        t('endOfDayReportsList.summary.totalLabel'),
        totals.balance,
        totals.cashTotal,
        totals.posTotal,
        totals.otherTotal,
        totals.cancelledTotal,
        totals.transactionCount,
        '',
      ]);
      totalRow.eachCell((cell, colNumber) => {
        if (colNumber >= 4) {
          cell.style = {
            font: { bold: true },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } },
            alignment: { horizontal: colNumber === 4 ? 'right' : 'center' },
          };
        }
      });
      
      // Sütun genişlikleri
      worksheet.columns = [
        { width: 8 },
        { width: 20 },
        { width: 18 },
        { width: 18 },
        { width: 15 },
        { width: 15 },
        { width: 15 },
        { width: 15 },
        { width: 12 },
        { width: 12 },
        { width: 18 },
      ];
      
      // Dosyayı indir
      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `End_Of_Day_List_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(new Blob([buffer]), fileName);
      
    } catch (err: any) {
      console.error('Excel export hatası:', err);
      setError(t('endOfDayReportsList.errors.excelCreateFailed'));
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
      <Box>
        {/* Üst Başlık */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
            {!isManagerRole && (
              <IconButton onClick={goBack} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <HistoryIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
            <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" fontWeight="bold">
              {t('endOfDayReportsList.title')}
            </Typography>
          </Box>
          
          {/* Sağ Üst Butonlar */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title={t('endOfDayReportsList.actions.refresh')}>
              <IconButton
                color="primary"
                onClick={handleFilter}
                disabled={loading || !companyId}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('endOfDayReportsList.actions.downloadExcel')}>
              <span>
                <IconButton
                  color="success"
                  onClick={handleExportExcel}
                  disabled={!dataLoaded || loadingExport || reports.length === 0}
                  size="small"
                  sx={{ 
                    bgcolor: 'success.main', 
                    color: 'white',
                    '&:hover': { bgcolor: 'success.dark' },
                    '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                  }}
                >
                  {loadingExport ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Filtreler */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Şube Filtresi (Autocomplete) */}
            <Grid item xs={6} sm={4} md={2}>
              <Autocomplete
                size="small"
                options={
                  isManagerRole && effectiveBranchId
                    ? branches.filter(b => b.id === effectiveBranchId)
                    : [{ id: 'ALL', name: t('endOfDayReportsList.common.all') }, ...branches.filter(b => b.id !== 'null')]
                }
                getOptionLabel={(option) => option.name}
                value={
                  isManagerRole && effectiveBranchId
                    ? branches.find(b => b.id === effectiveBranchId) || { id: effectiveBranchId, name: getBranchName(effectiveBranchId) || t('endOfDayReportsList.filters.yourBranch') }
                    : selectedBranch === 'ALL' 
                      ? { id: 'ALL', name: t('endOfDayReportsList.common.all') } 
                      : branches.find(b => b.id === selectedBranch) || { id: 'ALL', name: t('endOfDayReportsList.common.all') }
                }
                onChange={(_, newValue) => {
                  if (isManagerRole && effectiveBranchId) return; // Manager şube değiştiremez
                  setSelectedBranch(newValue?.id || 'ALL');
                }}
                disabled={isManagerRole && !!effectiveBranchId}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={isManagerRole && effectiveBranchId ? t('endOfDayReportsList.filters.yourBranchLocked') : t('endOfDayReportsList.filters.branch')} 
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableClearable
                fullWidth
              />
            </Grid>
            
            {/* Başlangıç Tarihi */}
            <Grid item xs={6} sm={4} md={2}>
              <DateTimePicker
                label={t('endOfDayReportsList.filters.startDate')}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                }}
                format="dd.MM.yyyy HH:mm"
              />
            </Grid>
            
            {/* Bitiş Tarihi */}
            <Grid item xs={6} sm={4} md={2}>
              <DateTimePicker
                label={t('endOfDayReportsList.filters.endDate')}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                }}
                format="dd.MM.yyyy HH:mm"
              />
            </Grid>
            
            {/* Filtrele Butonu */}
            <Grid item xs={12} sm={4} md={1.5}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFilter}
                disabled={loading || !companyId}
                startIcon={loading ? <CircularProgress size={16} /> : <FilterListIcon />}
                size="small"
                fullWidth
              >
                {loading ? t('endOfDayReportsList.common.loading') : t('endOfDayReportsList.actions.filter')}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Veri yüklenmeden önce bilgi mesajı */}
        {!dataLoaded && !loading && (
          <Box sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
            borderRadius: 2
          }}>
            <HistoryIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
              {t('endOfDayReportsList.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              {t('endOfDayReportsList.emptyState.beforeFilter')}
            </Typography>
          </Box>
        )}

        {/* Yükleniyor */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        )}

        {/* Veri yüklendikten sonra */}
        {dataLoaded && !loading && (
          <>
            {/* Tablo */}
            <Paper elevation={2} sx={{ borderRadius: 1, overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap', width: 60 }}>
                        {t('endOfDayReportsList.table.order')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' }}>
                        {t('endOfDayReportsList.table.branch')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' }}>
                        {t('endOfDayReportsList.table.dateRange')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' }}>
                        {t('endOfDayReportsList.table.balance')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' }}>
                        {t('endOfDayReportsList.table.cash')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' }}>
                        POS
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' }}>
                        {t('endOfDayReportsList.table.other')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' }}>
                        {t('endOfDayReportsList.table.cancelled')}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' }}>
                        {t('endOfDayReportsList.table.transaction')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' }}>
                        {t('endOfDayReportsList.table.createdAt')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell 
                          colSpan={10}
                          align="center"
                          sx={{ py: 4 }}
                        >
                          <Typography color="text.secondary">
                            {t('endOfDayReportsList.emptyState.noData')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedData.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            '&:nth-of-type(odd)': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {row.rowNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.branchId ? getBranchName(row.branchId) : t('endOfDayReportsList.common.all')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">
                              {formatDateTime(row.startDate)}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatDateTime(row.endDate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {formatCurrency(row.balance)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatCurrency(row.cashTotal)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatCurrency(row.posTotal)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color={row.otherTotal > 0 ? 'secondary.main' : 'text.secondary'}>
                              {formatCurrency(row.otherTotal)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color={row.cancelledTotal > 0 ? 'error.main' : 'text.secondary'}>
                              {formatCurrency(row.cancelledTotal)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={row.transactionCount} 
                              size="small" 
                              color="default"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDateTime(row.createdAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={reports.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={t('endOfDayReportsList.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              />
            </Paper>
            
            {/* ✅ YENİ: Genel Toplam Özeti */}
            {reports.length > 0 && (
              <Paper 
                elevation={2} 
                sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: 'success.50', 
                  border: '2px solid', 
                  borderColor: 'success.main',
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" color="success.dark" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon />
                  {t('endOfDayReportsList.summary.title', { count: reports.length })}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('endOfDayReportsList.summary.totalBalance')}</Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.main">
                        {formatCurrency(totals.balance)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('endOfDayReportsList.summary.totalCash')}</Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        {formatCurrency(totals.cashTotal)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('endOfDayReportsList.summary.totalPos')}</Typography>
                      <Typography variant="h6" fontWeight="bold" color="info.main">
                        {formatCurrency(totals.posTotal)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('endOfDayReportsList.summary.totalOther')}</Typography>
                      <Typography variant="h6" fontWeight="bold" color={totals.otherTotal > 0 ? 'secondary.main' : 'text.secondary'}>
                        {formatCurrency(totals.otherTotal)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('endOfDayReportsList.summary.totalCancelled')}</Typography>
                      <Typography variant="h6" fontWeight="bold" color={totals.cancelledTotal > 0 ? 'error.main' : 'text.secondary'}>
                        {formatCurrency(totals.cancelledTotal)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('endOfDayReportsList.summary.totalTransactions')}</Typography>
                      <Typography variant="h6" fontWeight="bold" color="warning.main">
                        {totals.transactionCount.toLocaleString('tr-TR')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default EndOfDayReportsList;
